import { Tabs as TabsPrimitive } from '@base-ui/react/tabs';

import { cn } from '../../cn';

/**
 * Segmented tabs in the shadcn style: a muted rounded track with the selected
 * trigger lifted onto a card-coloured block. shadcn-shaped API (Tabs /
 * TabsList / TabsTrigger / TabsContent) wrapping the @base-ui/react tabs
 * primitive — the same primitive family the other ui/ wrappers use (see
 * Sheet.tsx). The primitive provides the `role="tab"` / `aria-selected`
 * semantics and controlled `value` / `onValueChange`; these wrappers only add
 * the visual styling. The active tab is marked by the primitive's
 * `data-active` attribute and `state.active`.
 *
 * ## The strip scrolls sideways; it must never wrap
 *
 * A wrapped segmented track becomes two rows of pills inside one rounded
 * track, which reads as two controls. So "overflow horizontally, never wrap"
 * is a structural property of this component rather than a layout preference
 * of whoever mounts it, and the guard lives HERE (`max-w-full` +
 * `overflow-x-auto` on the list, `shrink-0` + `whitespace-nowrap` on the
 * trigger) instead of at each call site. `max-w-full` is what lets the list
 * overflow at all; without it the track grows past its parent instead.
 * Call sites keep the freedom to style the strip on top — FormView adds
 * scrollbar-hiding chrome for its pinned masthead. #1961.
 *
 * The selected block uses `bg-card` (this app's white surface), not
 * `bg-background`, which is only 2 lightness points from `bg-muted` in the
 * light theme and darker than the track in the dark theme.
 */

/**
 * The geometry and type shared with `ui/RouteTabs` (#2005), whose tabs are
 * LINKS rather than values and so cannot reuse this primitive itself. Exported
 * as constants rather than re-derived there so two strips that must look
 * identical cannot drift. The ACTIVE / IDLE strings are shared too; each strip
 * only decides WHEN to apply them (`state.active` here, NavLink's `isActive`
 * there).
 */
export const TAB_LIST_CLASS =
  'flex w-fit max-w-full items-center overflow-x-auto rounded-lg bg-muted p-[3px] text-muted-foreground';
export const TAB_TRIGGER_CLASS =
  'inline-flex h-[30px] shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent px-2 py-1 text-sm font-medium outline-none transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50';
export const TAB_TRIGGER_ACTIVE_CLASS = 'bg-card text-foreground shadow-sm dark:bg-input';
export const TAB_TRIGGER_IDLE_CLASS = 'text-muted-foreground hover:text-foreground';

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
  return <TabsPrimitive.Root data-slot="tabs" className={className} {...props} />;
}

function TabsList({ className, ...props }: TabsPrimitive.List.Props) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(TAB_LIST_CLASS, className)}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={(state) =>
        cn(
          // `shrink-0` is what makes the list scroll rather than squeeze: a
          // shrinkable trigger absorbs the deficit itself and ellipsizes its
          // label. `whitespace-nowrap` states the property actually meant —
          // `shrink-0` only holds the box at max-content while the trigger's
          // width stays content-derived, so a caller that sizes a trigger
          // (`flex-1`, `w-full`) or nests it in a constrained parent re-enables
          // wrapping.
          TAB_TRIGGER_CLASS,
          state.active ? TAB_TRIGGER_ACTIVE_CLASS : TAB_TRIGGER_IDLE_CLASS,
          typeof className === 'function' ? className(state) : className,
        )
      }
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn('outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
